import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { useMemo, useRef, useCallback } from 'react';
import getConfig from 'next/config';
import { useLazyQuery } from '@apollo/client';
import type Pubnub from 'pubnub';
import { GET_PUB_NUB_CONFIG } from '@graphql/operations/notifications/getPubNubConfig';
import { AUTHORIZE_PUB_NUB_ENTITY } from '@graphql/operations/notifications/authorizePubNubEntity';
import { PubNubClientType } from '@graphql/gql/graphql';

const { publicRuntimeConfig } = getConfig();

export const DEFAULT_PUBNUB_CONFIG = {
  keepAlive: true,
  keepAliveSettings: {
    keepAliveMsecs: 1000,
    freeSocketKeepAliveTimeout: 15000,
    timeout: 30000,
    maxSockets: Infinity,
    maxFreeSockets: 256
  }
};

const LOG = LoggerFactory.create('usePubNub');

// this hook is an abstraction on pubNub client
const usePubNubClient = () => {
  const pubNubClient = useRef<Pubnub>();
  return useMemo(() => {
    return {
      get: () => pubNubClient.current,
      set: client => {
        pubNubClient.current = client;
      }
    };
  }, []);
};

// This hook returns a method to initialize pubNubClient and add event listeners, if
// the client does not exist. if it does then returns the same.
export const usePubNub = () => {
  const pubNubClient = usePubNubClient();

  const [getPubNubConfig] = useLazyQuery(GET_PUB_NUB_CONFIG);

  const [getAuthPubNub] = useLazyQuery(AUTHORIZE_PUB_NUB_ENTITY);

  const getPubNubClient = useCallback(
    async ({ eventListeners, config }) => {
      const existingClient = pubNubClient.get();
      if (existingClient) {
        return existingClient;
      }
      if (!config) {
        LOG.error('cannot initialize pubNub client, no config found');
      }
      const Pubnub = await import('pubnub').then(module => module.default);
      const client = new Pubnub({
        ...DEFAULT_PUBNUB_CONFIG,
        ...config,
        logVerbosity: publicRuntimeConfig.PUBNUB_LOG_VERBOSITY === 'true',
        origin: publicRuntimeConfig.PUBNUB_CUSTOM_ORIGIN
      });
      client.addListener(eventListeners);
      pubNubClient.set(client);
      return client;
    },
    [pubNubClient]
  );

  const fetchPubNubConfig = async () => {
    const { data, error } = await getPubNubConfig({
      variables: {
        clientType: PubNubClientType.Web
      },
      fetchPolicy: 'cache-first'
    });
    if (error) {
      LOG.error('Fetch PubNub config error:', error);
    }
    return data?.pubNubConfig;
  };

  const getAuthorizePubNubEntity = async entityId => {
    const pubNubConfig = await fetchPubNubConfig();
    if (!pubNubConfig) return null;
    const authKey = pubNubConfig?.authKey;
    const response = await getAuthPubNub({
      fetchPolicy: 'cache-first',
      variables: { authKey, entityId }
    });
    return response?.data?.authorizePubNubEntity;
  };

  return {
    getPubNubClient,
    getCachedPubNubClient: pubNubClient.get,
    fetchPubNubConfig,
    getAuthorizePubNubEntity
  };
};
